import React, { useState } from "react";
import { useAuth } from "../utils/useAuth";
import { db, storage } from "../firebase";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function RefereeProfile() {
  const { user, profile } = useAuth();

  const [form, setForm] = useState({
    fullName: profile?.fullName || "",
    contactNumber: profile?.contactNumber || "",
    location: profile?.location || "",
    union: profile?.union || "",
    refereeCategory: profile?.refereeCategory || "",
    experience: profile?.experience || "",
    currentLeague: profile?.currentLeague || "",
  });

  const [loading, setLoading] = useState(false);

  // Load completed games where this referee was assigned
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "games"),
      where("status", "==", "completed")
    );

    const unsub = onSnapshot(q, (snap) => {
      const allGames = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Filter games where this referee was involved in any role
      const myGames = allGames.filter(g => 
        g.assignedRefereeId === user.uid ||
        g.assistantReferee1Id === user.uid ||
        g.assistantReferee2Id === user.uid ||
        g.assistant4Id === user.uid ||
        g.assistant5Id === user.uid ||
        g.timeKeeperId === user.uid ||
        g.playerMovementRecorderId === user.uid ||
        g.statisticianId === user.uid ||
        g.technicalZoneManagerId === user.uid
      );

      setCompletedGames(myGames);
    });

    return unsub;
  }, [user]);

  // Load billing rates
  useEffect(() => {
    async function loadRates() {
      if (!profile?.union) return;

      try {
        // Get all billing rates documents and find one that matches the referee's union
        const ratesSnap = await getDocs(collection(db, "billingRates"));
        
        // Try to find rates from a manager in the same union
        let foundRates = null;
        for (const rateDoc of ratesSnap.docs) {
          const managerId = rateDoc.id;
          const managerDoc = await getDoc(doc(db, "users", managerId));
          
          if (managerDoc.exists() && managerDoc.data().union === profile.union) {
            foundRates = rateDoc.data();
            break;
          }
        }

        // If no union match, use first available rates
        if (!foundRates && ratesSnap.docs.length > 0) {
          foundRates = ratesSnap.docs[0].data();
        }

        if (foundRates) {
          setDefaultRates({
            referee: foundRates.referee || 0,
            assistant1: foundRates.assistant1 || 0,
            assistant2: foundRates.assistant2 || 0,
            assistant4: foundRates.assistant4 || 0,
            assistant5: foundRates.assistant5 || 0,
            timekeeper: foundRates.timekeeper || 0,
            playermovement: foundRates.playermovement || 0,
            statistician: foundRates.statistician || 0,
            technicalzone: foundRates.technicalzone || 0,
          });
          setCompetitionRates(foundRates.competitionRates || {});
        }
      } catch (err) {
        console.error("Error loading rates:", err);
      }
    }
    
    loadRates();
  }, [profile]);

  // Determine role and get billing rate for a game
  function getGameDetails(game) {
    let role = "";
    let roleKey = "";
    
    if (game.assignedRefereeId === user.uid) {
      role = "Referee";
      roleKey = "referee";
    } else if (game.assistantReferee1Id === user.uid) {
      role = "AR1";
      roleKey = "assistant1";
    } else if (game.assistantReferee2Id === user.uid) {
      role = "AR2";
      roleKey = "assistant2";
    } else if (game.assistant4Id === user.uid) {
      role = "A4";
      roleKey = "assistant4";
    } else if (game.assistant5Id === user.uid) {
      role = "A5";
      roleKey = "assistant5";
    } else if (game.timeKeeperId === user.uid) {
      role = "Timekeeper";
      roleKey = "timekeeper";
    } else if (game.playerMovementRecorderId === user.uid) {
      role = "PMR";
      roleKey = "playermovement";
    } else if (game.statisticianId === user.uid) {
      role = "Statistician";
      roleKey = "statistician";
    } else if (game.technicalZoneManagerId === user.uid) {
      role = "TZM";
      roleKey = "technicalzone";
    }

    // Get rate: competition-specific first, then default
    let rate = 0;
    if (game.competition && competitionRates[game.competition]?.[roleKey]) {
      rate = competitionRates[game.competition][roleKey];
    } else {
      rate = defaultRates[roleKey] || 0;
    }

    return { role, rate };
  }

  // Calculate total earnings
  const totalEarnings = completedGames.reduce((sum, game) => {
    const { rate } = getGameDetails(game);
    return sum + parseFloat(rate || 0);
  }, 0);

  // Handle input changes
  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function uploadOptionalFile(file, path) {
    if (!file) return null; // file optional

    const fileRef = ref(storage, `${path}/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  }

  async function handleSave() {
    setLoading(true);

    const idFile = document.getElementById("idDoc").files[0];
    const sruFile = document.getElementById("saruCard").files[0];
    const medFile = document.getElementById("medical").files[0];

    let idURL = await uploadOptionalFile(idFile, `referees/${user.uid}/id`);
    let saruURL = await uploadOptionalFile(sruFile, `referees/${user.uid}/saru`);
    let medURL = await uploadOptionalFile(medFile, `referees/${user.uid}/medical`);

    await updateDoc(doc(db, "users", user.uid), {
      ...form,
      idDocumentURL: idURL || profile?.idDocumentURL || "",
      saruCardURL: saruURL || profile?.saruCardURL || "",
      medicalCertURL: medURL || profile?.medicalCertURL || "",
      updatedAt: new Date(),
    });

    setLoading(false);
    alert("Profile Updated Successfully!");
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Referee Profile</h2>

      <div className="space-y-4">

        {/* TEXT FIELDS */}
        <input
          className="w-full p-2 border rounded"
          name="fullName"
          placeholder="Full Name"
          value={form.fullName}
          onChange={handleChange}
        />

        <input
          className="w-full p-2 border rounded"
          name="contactNumber"
          placeholder="Contact Number"
          value={form.contactNumber}
          onChange={handleChange}
        />

        <input
          className="w-full p-2 border rounded"
          name="location"
          placeholder="Location"
          value={form.location}
          onChange={handleChange}
        />

        <select
          className="w-full p-2 border rounded"
          name="union"
          value={form.union}
          onChange={handleChange}
        >
          <option value="">Select Union</option>
          <option value="Blue Bulls">Blue Bulls</option>
          <option value="Golden Lions">Golden Lions</option>
          <option value="Sharks">Sharks</option>
          <option value="Western Province">Western Province</option>
          <option value="Cheetahs">Cheetahs</option>
          <option value="Pumas">Pumas</option>
          <option value="Griquas">Griquas</option>
          <option value="Eastern Province">Eastern Province</option>
          <option value="Border">Border</option>
          <option value="Griffons">Griffons</option>
          <option value="Boland">Boland</option>
          <option value="SWD Eagles">SWD Eagles</option>
          <option value="Leopards">Leopards</option>
          <option value="Valke">Valke</option>
        </select>

        <input
          className="w-full p-2 border rounded"
          name="experience"
          placeholder="Experience Level"
          value={form.experience}
          onChange={handleChange}
        />

        <select
          className="w-full p-2 border rounded"
          name="currentLeague"
          value={form.currentLeague}
          onChange={handleChange}
        >
          <option value="">Select Current League</option>
          <option value="1st League">1st League</option>
          <option value="2nd League">2nd League</option>
          <option value="3rd League">3rd League</option>
          <option value="4th League">4th League</option>
          <option value="Biginner">Beginner</option>
          <option value="Social">Social</option>
        </select>

        {/* SELECT */}
        <select
          className="w-full p-2 border rounded"
          name="refereeCategory"
          value={form.refereeCategory}
          onChange={handleChange}
        >
          <option value="">Select Category</option>
          <option value="main">Main Referee</option>
          <option value="assistant">Assistant Referee</option>
          <option value="both">Both</option>
        </select>

        <hr className="my-6" />

        <h3 className="font-semibold text-lg">Optional Document Uploads</h3>

        {/* OPTIONAL FILE UPLOADS */}
        <div>
          <label>ID Document (Optional)</label>
          <input type="file" id="idDoc" className="w-full" />
        </div>

        <div>
          <label>SARU Card (Optional)</label>
          <input type="file" id="saruCard" className="w-full" />
        </div>

        <div>
          <label>Medical Certificate (Optional)</label>
          <input type="file" id="medical" className="w-full" />
        </div>

        <button
          onClick={handleSave}
          className="w-full bg-green-700 text-white py-2 rounded mt-4"
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Profile"}
        </button>
      </div>
    </div>
  );
}
